import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { authColors, shadows } from '../../styles/colors';

interface OtpVerificationProps {
  email: string;
  userData?: { name: string; company?: string };
  onVerified?: () => void;
  onBack?: () => void;
  type?: 'signup' | 'email' | 'recovery'; // 'signup' for new account, 'email' for existing account verification, 'recovery' for password reset
  context?: 'signup' | 'login' | 'forgot'; // Context to adapt the message
}

const OtpVerification: React.FC<OtpVerificationProps> = ({ email, userData, onVerified, onBack, type = 'signup', context = 'signup' }) => {
  const { t } = useI18n();
  const { sendOtp, verifyOtp } = useAuth();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [rateLimitSeconds, setRateLimitSeconds] = useState<number | null>(60); // Start with 60s timer
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer for rate limiting
  useEffect(() => {
    if (rateLimitSeconds === null || rateLimitSeconds <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setRateLimitSeconds((prev) => {
        if (prev === null || prev <= 1) {
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [rateLimitSeconds]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (value && index === 5 && newOtp.every(digit => digit !== '')) {
      handleVerify(newOtp.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace: clear current and focus previous
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Arrow keys: navigate
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    const digits = pastedData.replace(/\D/g, '').slice(0, 6).split('');
    
    if (digits.length === 6) {
      const newOtp = [...otp];
      digits.forEach((digit, index) => {
        newOtp[index] = digit;
      });
      setOtp(newOtp);
      inputRefs.current[5]?.focus();
      // Auto-submit
      setTimeout(() => {
        handleVerify(newOtp.join(''));
      }, 100);
    }
  };

  const handleVerify = async (otpValue?: string) => {
    const code = otpValue || otp.join('');
    if (code.length !== 6) {
      setError(t('auth.otpInvalidLength'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error } = await verifyOtp(email, code, type);
      if (error) {
        if (error.message?.toLowerCase().includes('invalid') || 
            error.message?.toLowerCase().includes('expired')) {
          setError(t('auth.otpInvalid'));
        } else {
          setError(error.message || t('auth.otpVerifyError'));
        }
        // Clear OTP on error
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      } else {
        // Success - onVerified will be called by parent
        if (onVerified) {
          onVerified();
        }
      }
    } catch (err) {
      setError(t('auth.otpVerifyError'));
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (rateLimitSeconds !== null) return;
    
    setResending(true);
    setError(null);
    setRateLimitSeconds(null);
    
    try {
      // Must match the OTP purpose: recovery resend previously defaulted to signup and broke verify.
      const otpType = type === 'recovery' ? 'recovery' : 'signup';
      const { error } = await sendOtp(email, otpType === 'signup' ? userData : undefined, otpType);
      if (error) {
        const isRateLimited = (error as any).isRateLimited;
        const waitSeconds = (error as any).waitSeconds;
        if (isRateLimited) {
          setRateLimitSeconds(typeof waitSeconds === 'number' ? waitSeconds : 60);
          setError(null); // Timer message is enough
        } else {
          setError(t('auth.otpResendError'));
        }
      } else {
        setError(null);
        // Reset timer to 60 seconds after successful resend
        setRateLimitSeconds(60);
        // Clear OTP and refocus
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError(t('auth.otpResendError'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {context === 'login' ? t('auth.otpTitleLogin') : context === 'forgot' ? t('auth.otpTitleForgot') : t('auth.otpTitle')}
        </h2>
        <p className="text-gray-600 text-sm mb-1">
          {context === 'login' 
            ? t('auth.otpMessageLogin') 
            : context === 'forgot' 
            ? t('auth.otpMessageForgot') 
            : t('auth.otpMessage')} <span className="font-semibold text-gray-900">{email}</span>
        </p>
        {context === 'login' && (
          <p className="text-gray-500 text-xs mt-2">
            {t('auth.otpNotYourAccount')}
          </p>
        )}
        <p className="text-pink-600 text-xs">{t('auth.otpSpamWarning')}</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
            {t('auth.otpCodeLabel')}
          </label>
          <div className="flex justify-center gap-2">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="w-12 h-14 text-center text-2xl font-semibold border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                disabled={loading}
              />
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-700 text-sm text-center">{error}</p>
          </div>
        )}

        {loading && (
          <div className="flex justify-center items-center py-2">
            <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="ml-2 text-sm text-gray-600">{t('auth.otpVerifying')}</span>
          </div>
        )}

        <div className="space-y-3">
          {rateLimitSeconds !== null && rateLimitSeconds > 0 && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                {t('auth.otpResendAvailableIn', { seconds: rateLimitSeconds })}
              </span>
            </div>
          )}
          
          <button
            onClick={handleResend}
            disabled={resending || (rateLimitSeconds !== null && rateLimitSeconds > 0)}
            className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg ${shadows.medium} text-sm font-medium text-white ${authColors.primaryButton} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {resending ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('auth.otpResending')}
              </div>
            ) : (
              t('auth.otpResend')
            )}
          </button>
        </div>

        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className={`w-full ${authColors.authLink} py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors`}
          >
            {t('auth.backToSignUp')}
          </button>
        )}
      </div>
    </div>
  );
};

export default OtpVerification;

